import path from 'node:path'
import type { ResolvedBetterstartConfig } from '@core-engine/config/types.js'
import { ensureDir, safeWriteFile } from '@core-engine/utils/fs.js'
import {
  applyAdminNamespaceToContent,
  applyAdminNamespaceToPath
} from '@next-adapter/config/namespace.js'
import { readTemplate } from '@next-adapter/template-reader.js'

export interface LayoutScaffoldOptions {
  cwd: string
  config: ResolvedBetterstartConfig
}

/**
 * Create all Admin route structures: layouts, login, dashboard, users, settings.
 */
export function scaffoldLayout({ cwd, config }: LayoutScaffoldOptions): string[] {
  const created: string[] = []
  const namespace = config.frameworkConfig.next.namespace

  function write(targetPath: string, content: string): void {
    const relPath = path.isAbsolute(targetPath) ? path.relative(cwd, targetPath) : targetPath
    if (relPath.startsWith('..') || path.isAbsolute(relPath)) {
      throw new Error(`Refusing to scaffold layout outside project root: ${targetPath}`)
    }

    const namespacedRelPath = applyAdminNamespaceToPath(relPath, namespace)
    const fullPath = path.resolve(cwd, namespacedRelPath)
    ensureDir(path.dirname(fullPath))
    if (safeWriteFile(fullPath, applyAdminNamespaceToContent(content, namespace))) {
      created.push(namespacedRelPath)
    }
  }

  // Derive base Admin layout dir from pages path
  // pages = "./src/app/(admin)/admin/(authenticated)" → adminDir = "./src/app/(admin)/admin"
  const adminDir = path.dirname(config.paths.pages)

  // --- Shared Admin layout ---
  write(path.join(adminDir, 'layout.tsx'), readTemplate('pages/admin-layout.tsx'))

  // --- Authenticated layout ---
  write(path.join(config.paths.pages, 'layout.tsx'), readTemplate('pages/authenticated-layout.tsx'))
  write(path.join(config.paths.pages, 'auth-gate-rsc.tsx'), readTemplate('pages/auth-gate-rsc.tsx'))

  // --- Login page ---
  write(path.join(config.paths.login, 'page.tsx'), readTemplate('pages/login-page.tsx'))
  write(
    path.join(config.paths.login, 'login-page-rsc.tsx'),
    readTemplate('pages/login-page-rsc.tsx')
  )
  write(
    path.join(config.paths.login, 'login-page-skeleton.tsx'),
    readTemplate('pages/login-page-skeleton.tsx')
  )
  write(path.join(config.paths.login, 'login-form.tsx'), readTemplate('pages/login-form.tsx'))

  const authDir = path.dirname(config.paths.login)

  // --- Forgot password page ---
  const forgotPasswordDir = path.join(authDir, 'forgot-password')
  write(path.join(forgotPasswordDir, 'page.tsx'), readTemplate('pages/forgot-password-page.tsx'))
  write(
    path.join(forgotPasswordDir, 'forgot-password-page-skeleton.tsx'),
    readTemplate('pages/forgot-password-page-skeleton.tsx')
  )
  write(
    path.join(forgotPasswordDir, 'forgot-password-form.tsx'),
    readTemplate('pages/forgot-password-form.tsx')
  )

  // --- Reset password page ---
  const resetPasswordDir = path.join(authDir, 'reset-password')
  write(path.join(resetPasswordDir, 'page.tsx'), readTemplate('pages/reset-password-page.tsx'))
  write(
    path.join(resetPasswordDir, 'reset-password-page-skeleton.tsx'),
    readTemplate('pages/reset-password-page-skeleton.tsx')
  )
  write(
    path.join(resetPasswordDir, 'reset-password-form.tsx'),
    readTemplate('pages/reset-password-form.tsx')
  )

  // --- Dashboard ---
  write(path.join(config.paths.pages, 'page.tsx'), readTemplate('pages/dashboard-page.tsx'))

  // --- Users pages ---
  const usersDir = path.join(config.paths.pages, 'users')
  write(path.join(usersDir, 'page.tsx'), readTemplate('pages/users/users-page.tsx'))
  write(
    path.join(usersDir, 'users-page-skeleton.tsx'),
    readTemplate('pages/users/users-page-skeleton.tsx')
  )
  write(
    path.join(usersDir, 'users-page-content.tsx'),
    readTemplate('pages/users/users-page-content.tsx')
  )
  write(path.join(usersDir, 'users-table.tsx'), readTemplate('pages/users/users-table.tsx'))
  write(path.join(usersDir, 'columns.tsx'), readTemplate('pages/users/columns.tsx'))
  write(
    path.join(usersDir, 'create-user-dialog.tsx'),
    readTemplate('pages/users/create-user-dialog.tsx')
  )
  write(
    path.join(usersDir, 'delete-user-dialog.tsx'),
    readTemplate('pages/users/delete-user-dialog.tsx')
  )
  write(
    path.join(usersDir, 'edit-role-dialog.tsx'),
    readTemplate('pages/users/edit-role-dialog.tsx')
  )
  write(
    path.join(usersDir, 'change-password-dialog.tsx'),
    readTemplate('pages/users/change-password-dialog.tsx')
  )

  // --- Settings section (sidebar layout + forms + webhooks) ---
  const settingsDir = path.join(config.paths.pages, 'settings')
  write(path.join(settingsDir, 'layout.tsx'), readTemplate('pages/settings/settings-layout.tsx'))
  const settingsFormsDir = path.join(settingsDir, 'forms')
  write(
    path.join(settingsFormsDir, 'page.tsx'),
    readTemplate('pages/settings/forms/forms-settings-page.tsx')
  )
  write(
    path.join(settingsFormsDir, 'forms-settings-page-skeleton.tsx'),
    readTemplate('pages/settings/forms/forms-settings-page-skeleton.tsx')
  )
  write(
    path.join(settingsFormsDir, 'forms-settings-page-content.tsx'),
    readTemplate('pages/settings/forms/forms-settings-page-content.tsx')
  )
  write(
    path.join(settingsFormsDir, 'edit-form-notifications-dialog.tsx'),
    readTemplate('pages/settings/forms/edit-form-notifications-dialog.tsx')
  )
  const settingsWebhooksDir = path.join(settingsDir, 'webhooks')
  write(
    path.join(settingsWebhooksDir, 'page.tsx'),
    readTemplate('pages/settings/webhooks/webhooks-page.tsx')
  )
  write(
    path.join(settingsWebhooksDir, 'webhooks-page-skeleton.tsx'),
    readTemplate('pages/settings/webhooks/webhooks-page-skeleton.tsx')
  )
  write(
    path.join(settingsWebhooksDir, 'webhooks-page-content.tsx'),
    readTemplate('pages/settings/webhooks/webhooks-page-content.tsx')
  )
  write(
    path.join(settingsWebhooksDir, 'webhooks-table.tsx'),
    readTemplate('pages/settings/webhooks/webhooks-table.tsx')
  )
  write(
    path.join(settingsWebhooksDir, 'webhooks-columns.tsx'),
    readTemplate('pages/settings/webhooks/webhooks-columns.tsx')
  )
  write(
    path.join(settingsWebhooksDir, 'webhook-actions.tsx'),
    readTemplate('pages/settings/webhooks/webhook-actions.tsx')
  )
  write(
    path.join(settingsWebhooksDir, 'webhook-enabled-switch.tsx'),
    readTemplate('pages/settings/webhooks/webhook-enabled-switch.tsx')
  )
  write(
    path.join(settingsWebhooksDir, 'webhook-endpoint-dialog.tsx'),
    readTemplate('pages/settings/webhooks/webhook-endpoint-dialog.tsx')
  )
  write(
    path.join(settingsWebhooksDir, 'delete-webhook-dialog.tsx'),
    readTemplate('pages/settings/webhooks/delete-webhook-dialog.tsx')
  )
  write(
    path.join(settingsWebhooksDir, 'webhooks-logs-page-content.tsx'),
    readTemplate('pages/settings/webhooks/webhooks-logs-page-content.tsx')
  )
  write(
    path.join(settingsWebhooksDir, 'webhooks-logs-table.tsx'),
    readTemplate('pages/settings/webhooks/webhooks-logs-table.tsx')
  )
  write(
    path.join(settingsWebhooksDir, 'webhooks-logs-columns.tsx'),
    readTemplate('pages/settings/webhooks/webhooks-logs-columns.tsx')
  )
  write(
    path.join(settingsWebhooksDir, 'logs', 'page.tsx'),
    readTemplate('pages/settings/webhooks/webhooks-logs-page.tsx')
  )

  // --- Media pages ---
  const mediaDir = path.join(config.paths.pages, 'media')
  write(path.join(mediaDir, 'page.tsx'), readTemplate('pages/media/media-page.tsx'))
  write(
    path.join(mediaDir, 'media-page-skeleton.tsx'),
    readTemplate('pages/media/media-page-skeleton.tsx')
  )
  write(
    path.join(mediaDir, 'media-page-content.tsx'),
    readTemplate('pages/media/media-page-content.tsx')
  )

  // --- Audit log under Settings ---
  const auditLogDir = path.join(config.paths.pages, 'settings', 'audit-log')
  write(
    path.join(auditLogDir, 'page.tsx'),
    readTemplate('pages/settings/audit-log/audit-log-page.tsx')
  )
  write(
    path.join(auditLogDir, 'audit-log-page-skeleton.tsx'),
    readTemplate('pages/settings/audit-log/audit-log-page-skeleton.tsx')
  )
  write(
    path.join(auditLogDir, 'audit-log-page-content.tsx'),
    readTemplate('pages/settings/audit-log/audit-log-page-content.tsx')
  )
  write(
    path.join(auditLogDir, 'audit-log-table.tsx'),
    readTemplate('pages/settings/audit-log/audit-log-table.tsx')
  )
  write(
    path.join(auditLogDir, 'audit-log-columns.tsx'),
    readTemplate('pages/settings/audit-log/audit-log-columns.tsx')
  )
  write(
    path.join(auditLogDir, 'audit-log-detail-drawer.tsx'),
    readTemplate('pages/settings/audit-log/audit-log-detail-drawer.tsx')
  )
  write(
    path.join(auditLogDir, 'audit-log-detail-row.tsx'),
    readTemplate('pages/settings/audit-log/audit-log-detail-row.tsx')
  )

  // --- Account layout + Profile page ---
  const accountDir = path.join(adminDir, '(account)')
  write(path.join(accountDir, 'layout.tsx'), readTemplate('pages/account-layout.tsx'))
  write(path.join(accountDir, 'account-shell-rsc.tsx'), readTemplate('pages/account-shell-rsc.tsx'))
  write(
    path.join(accountDir, 'minimal-account-shell.tsx'),
    readTemplate('pages/minimal-account-shell.tsx')
  )

  const profileDir = path.join(accountDir, 'profile')
  write(path.join(profileDir, 'page.tsx'), readTemplate('pages/profile/profile-page.tsx'))
  write(path.join(profileDir, 'profile-form.tsx'), readTemplate('pages/profile/profile-form.tsx'))

  // --- Profile action + Sign-out component ---
  write(
    path.join(config.paths.admin, 'lib', 'actions', 'profile', 'index.ts'),
    readTemplate('lib/actions/profile/index.ts')
  )
  write(
    path.join(config.paths.admin, 'components', 'layouts', 'admin-sign-out.tsx'),
    readTemplate('components/layouts/admin-sign-out.tsx')
  )

  return created
}
